/**
 * product-reports-api.ts — zestawienia towarowe: parametry w adresie i pobieranie danych.
 *
 * W odróżnieniu od raportów sprzedaży i magazynu tutaj CAŁE liczenie robi API: obrót wymaga
 * pozycji dokumentów, a progi min/max kart towarów, i korelowanie trzech źródeł w przeglądarce
 * nie kupiłoby niczego poza złożonością.
 */

import {
  HM_PRODUCT_REPORTS,
  type HmProductReportKind,
  hmProductReportNeedsLimits,
  hmProductReportNeedsTurnover,
  type HmProductReportResponse,
  type HmProductReportTotals,
} from '@saas/shared';

import type { RangePreset } from '@/components/handel-magazyn/HmListShell';
import { api } from '@/lib/api';

export interface ProductReportParams {
  kind: HmProductReportKind;
  preset: RangePreset;
  dateFrom: string;
  dateTo: string;
  warehouse: string | null;
  includeEmpty: boolean;
  ids: number[];
}

/** Klucze i18n wypisane wprost — klucz sklejany to martwe pole bramki tłumaczeń. */
const TITLE_KEYS: Record<HmProductReportKind, string> = {
  'obroty-za-okres': 'tenant.handelMagazyn.productReports.turnover.title',
  'stany-na-dzien': 'tenant.handelMagazyn.productReports.stockOnDate.title',
  'stany-i-obroty': 'tenant.handelMagazyn.productReports.stockAndTurnover.title',
  'stany-i-rezerwacje': 'tenant.handelMagazyn.productReports.stockAndReservations.title',
  'ponizej-minimum': 'tenant.handelMagazyn.productReports.belowMin.title',
  'powyzej-maksimum': 'tenant.handelMagazyn.productReports.aboveMax.title',
  zalegajace: 'tenant.handelMagazyn.productReports.slowMoving.title',
  rotacja: 'tenant.handelMagazyn.productReports.rotation.title',
};

export const PRODUCT_REPORT_KINDS = HM_PRODUCT_REPORTS;

export function isProductReportKind(value: string | undefined): value is HmProductReportKind {
  return !!value && (HM_PRODUCT_REPORTS as readonly string[]).includes(value);
}

export function productReportPath(kind: HmProductReportKind): string {
  return `/handel-magazyn/towary/zestawienia/${kind}`;
}

export function productReportTitleKey(kind: HmProductReportKind): string {
  return TITLE_KEYS[kind];
}

/**
 * Raporty, których Symfonia nie da się odwzorować wiernie — ekran musi to powiedzieć wprost,
 * a nie pokazać liczby wyglądające na coś, czym nie są.
 */
export function productReportCaveatKey(kind: HmProductReportKind): string | null {
  if (kind === 'stany-na-dzien') return 'tenant.handelMagazyn.productReports.caveat.stockOnDate';
  if (kind === 'rotacja') return 'tenant.handelMagazyn.productReports.caveat.rotation';
  return null;
}

export function defaultProductParams(
  kind: HmProductReportKind,
  from: string,
  to: string,
): ProductReportParams {
  return {
    kind,
    preset: 'custom',
    dateFrom: from,
    dateTo: to,
    warehouse: null,
    includeEmpty: false,
    ids: [],
  };
}

export function toProductSearchParams(p: ProductReportParams): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('kind', p.kind);
  sp.set('preset', p.preset);
  sp.set('dateFrom', p.dateFrom);
  sp.set('dateTo', p.dateTo);
  if (p.warehouse) sp.set('warehouse', p.warehouse);
  if (p.includeEmpty) sp.set('includeEmpty', 'true');
  if (p.ids.length > 0) sp.set('ids', p.ids.join(','));
  return sp;
}

export function parseProductSearchParams(
  sp: URLSearchParams,
  fallbackKind: HmProductReportKind,
): ProductReportParams {
  const kindRaw = sp.get('kind') ?? '';
  return {
    kind: isProductReportKind(kindRaw) ? kindRaw : fallbackKind,
    preset: (sp.get('preset') as RangePreset) || 'custom',
    dateFrom: sp.get('dateFrom') ?? '',
    dateTo: sp.get('dateTo') ?? '',
    warehouse: sp.get('warehouse') || null,
    includeEmpty: sp.get('includeEmpty') === 'true',
    ids: (sp.get('ids') ?? '')
      .split(',')
      .map((raw) => Number(raw))
      .filter((n) => Number.isInteger(n) && n > 0),
  };
}

/** Czy raport w ogóle używa zakresu dat — przy samych stanach data niczego nie zmienia. */
export function productReportUsesDates(kind: HmProductReportKind): boolean {
  return hmProductReportNeedsTurnover(kind);
}

export { hmProductReportNeedsLimits, hmProductReportNeedsTurnover };

export type ProductReportResult = HmProductReportResponse & { totals: HmProductReportTotals };

export async function fetchProductReport(
  companyId: string,
  params: ProductReportParams,
  signal?: AbortSignal,
): Promise<ProductReportResult> {
  const sp = toProductSearchParams(params);
  sp.set('companyId', companyId);
  sp.delete('preset');
  const res = await api.get<ProductReportResult>(`/handel-magazyn/reports/products?${sp}`, {
    signal,
  });
  return res.data;
}
